import { useEffect, useMemo, useState } from 'react';
import {
  LESSON_1_1_PROMPTS,
  useLocalAnswer,
} from '../lib/answerStore';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Self-contained visual preview of Lesson 1.1.
 *
 * Does NOT hit the API. Renders lesson content as structured sections so we
 * can iterate on the visual experience without seeding the DB.
 *
 * Route: /preview/lesson/1.1
 */

type SectionKind =
  | 'cold-open'
  | 'from-last-time'
  | 'teaching'
  | 'your-turn'
  | 'quick-check'
  | 'lock-card';

export default function LessonPreviewPage() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  // Scroll-spy: highlight the section currently centered in the viewport.
  useEffect(() => {
    const ids: Array<{ id: string; index: number }> = [
      { id: 'sec-cold-open', index: 0 },
      { id: 'sec-from-last-time', index: 1 },
      { id: 'sec-teaching', index: 2 },
      { id: 'sec-your-turn', index: 3 },
      { id: 'sec-quick-check', index: 4 },
      { id: 'sec-lock-card', index: 5 },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const found = ids.find((x) => x.id === visible[0].target.id);
          if (found) setActiveIndex(found.index);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    ids.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      {/* Top progress bar */}
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Dashboard
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main 3-column layout */}
      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_320px]">
        {/* Left rail: section nav */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>

        {/* Center: lesson content */}
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>

        {/* Right rail: AI panel placeholder */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 1 · Foundations · Lesson 1.1
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        What You Are Here to Learn
      </h1>
      <div className="mt-4 flex items-center gap-4 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 20 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Character · Fluency · Judgment</span>
        <span className="h-3 w-px bg-line" />
        <span>No AI in this lesson</span>
      </div>
    </header>
  );
}

// ---------- Section: Cold Open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      kind="cold-open"
      label="Cold open"
      eyebrow="A real scene, no theory."
    >
      <p>
        Picture two students in the same English class, both writing a five-paragraph essay on the same prompt.
      </p>

      <div className="my-6 grid gap-4 sm:grid-cols-2">
        <StudentCard
          label="Student A"
          time="11 minutes"
          tone="slick"
          body="Opens ChatGPT, types the prompt, pastes the output into their doc. Rewords a few sentences so it does not get flagged. Hits submit. Moves on."
        />
        <StudentCard
          label="Student B"
          time="90 minutes"
          tone="careful"
          body="Opens a blank doc. Spends 15 minutes thinking, writes a rough thesis, lists three reasons, then asks an AI to push back. AI points out two weak claims. Student B fixes them. Then asks AI to draft a counter-argument so they can rebut it."
        />
      </div>

      <p>Both turn in essays that look about the same on paper.</p>

      <p className="mt-6 border-l-2 border-amber pl-5 text-lg font-medium text-text">
        Five years from now, only one of them has a job.
      </p>
    </Section>
  );
}

function StudentCard({
  label,
  time,
  tone,
  body,
}: {
  label: string;
  time: string;
  tone: 'slick' | 'careful';
  body: string;
}) {
  const accent =
    tone === 'slick' ? 'border-line bg-surface' : 'border-amber/40 bg-elevated';
  return (
    <div className={`rounded-lg border ${accent} p-5`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-widest text-muted">
          {label}
        </span>
        <span className="text-xs text-muted">{time}</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-text">{body}</p>
    </div>
  );
}

// ---------- Section: From Last Time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      kind="from-last-time"
      label="From last time"
      eyebrow="Retrieval beats re-reading. Reach for the answer first."
    >
      <div className="rounded-lg border border-line bg-surface/60 p-6">
        <p className="text-sm text-muted">
          This is the first lesson, so nothing to retrieve yet. Starting in Lesson 1.2, this section will quiz you on prior material before each new lesson begins. Answer in your head. No looking back.
        </p>
      </div>
    </Section>
  );
}

// ---------- Section: Teaching ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      kind="teaching"
      label="The teaching"
      eyebrow="The material. Concrete first, principle second."
    >
      <Subhead>Why we are starting here</Subhead>
      <p>
        The next 8 weeks will teach you a lot of technical things about AI. How it works. The vocabulary. How to direct it. How to spot when it is lying. How to build with it.
      </p>
      <p>But none of that matters if you are Student A.</p>
      <p>
        Student A is not lazy. Student A is just doing what makes sense in the short term. The output looks good. The grade is fine. The teacher does not catch it. Why think harder than you have to?
      </p>
      <p>
        Here is why: every time Student A skips the thinking, they get a little weaker at it. Not in a dramatic way. Just a tiny bit each time. By senior year of high school, they cannot write an argument without AI. By the second year of college, they cannot defend one. By their first real job, they cannot explain their own work in a meeting.
      </p>
      <p>
        The skill is not gone. It just never got built. And the people hiring will be able to tell.
      </p>

      <Subhead>The bet this program is making</Subhead>
      <p>
        The students who win the next decade will not be the ones who use AI the most. They will be the ones who think the hardest <em>before</em> they use AI, and the hardest <em>about</em> what AI gives them back.
      </p>
      <p className="text-text">That is what we are training. Not prompt skills. Judgment.</p>

      <div className="my-8 grid gap-4 sm:grid-cols-3">
        <ThreadCard
          number="01"
          name="Real fluency"
          body="You will know how AI actually works. Not in metaphors. In mechanics. By Week 8 you can hold a real conversation about this field."
        />
        <ThreadCard
          number="02"
          name="Auditing AI"
          body="A working muscle for spotting when AI is wrong, biased, shallow, or making things up. The rarest skill in the field right now."
        />
        <ThreadCard
          number="03"
          name="Posture"
          body="Treating AI as a teammate that does work with you and for you. Not a replacement for human connection and communication."
        />
      </div>

      <p>
        Those three threads run through every lesson. You will not get a "character module" or an "ethics chapter." It is woven through every assignment, every audit, every project.
      </p>

      <Subhead>How the next 8 weeks are built</Subhead>
      <p>
        You get about 3 hours of work each week. That is on purpose. AI gives you superpowers. Three hours a week, used well, beats 10 hours of grinding through the wrong things.
      </p>

      <WeekMap />

      <Subhead>How each lesson is built</Subhead>
      <p>
        Same shape every time. Once you see it twice, you stop noticing it, and it just becomes how you learn.
      </p>
      <LessonShapeDiagram />
      <p className="mt-4 text-sm text-muted">
        Two reasons for this shape. First, education research is consistent: pulling information back out of your memory beats re-reading it, every time. Second, real fluency is built by using the thing, not just hearing about it. So every lesson has a "your turn."
      </p>

      <Subhead>A non-negotiable for this program</Subhead>
      <div className="my-4 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
          The honor system
        </p>
        <p className="mt-3 text-text">
          You are going to be tempted, especially in audit exercises, to skip the thinking part and just look at the answer. Do not.
        </p>
        <p className="mt-3">
          The audit work in this program is the most valuable thing you will learn. It is also the only part that is easy to fake. Skip the thinking and you will not build the muscle. You will know what an audit looks like without being able to do one. That is worse than not learning it at all, because you will think you have the skill and then get embarrassed the first time you need it.
        </p>
        <p className="mt-3 text-text">
          Nobody is going to grade you. The only person who knows whether you actually did the thinking is you. Take it seriously or you will waste your time and not improve in any measurable way.
        </p>
      </div>
    </Section>
  );
}

function ThreadCard({
  number,
  name,
  body,
}: {
  number: string;
  name: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <div className="font-mono text-[11px] uppercase tracking-widest text-amber">
        Thread {number}
      </div>
      <div className="mt-2 text-lg font-semibold text-text">{name}</div>
      <p className="mt-2 text-sm leading-relaxed text-muted">{body}</p>
    </div>
  );
}

function WeekMap() {
  const phases = [
    { weeks: 'Weeks 1-2', name: 'Foundation', body: 'How AI actually works, the vocabulary, and your first audits.' },
    { weeks: 'Weeks 3-4', name: 'Application', body: 'Prompting, workflows, and directing multiple AI agents at once.' },
    { weeks: 'Weeks 5-6', name: 'Real project', body: 'You build and ship something using everything from Weeks 1-4.' },
    { weeks: 'Weeks 7-8', name: 'Industry & capstone', body: 'How AI is reshaping work, and what you do with that.' },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-lg border border-line bg-surface">
      {phases.map((p, i) => (
        <div
          key={p.weeks}
          className={`grid grid-cols-[120px_140px_minmax(0,1fr)] items-center gap-4 px-5 py-4 ${
            i !== phases.length - 1 ? 'border-b border-line' : ''
          }`}
        >
          <div className="font-mono text-[11px] uppercase tracking-widest text-amber">
            {p.weeks}
          </div>
          <div className="font-semibold text-text">{p.name}</div>
          <div className="text-sm text-muted">{p.body}</div>
        </div>
      ))}
    </div>
  );
}

function LessonShapeDiagram() {
  const steps = [
    { n: 1, name: 'Cold open', body: 'A real scene. No theory.' },
    { n: 2, name: 'From last time', body: 'Reach for the answer.' },
    { n: 3, name: 'Teaching', body: 'Concrete, then principle.' },
    { n: 4, name: 'Your turn', body: 'You do it before we compare.' },
    { n: 5, name: 'Quick check', body: 'Find what did not stick.' },
    { n: 6, name: 'Lock card', body: 'Screenshot-ready summary.' },
  ];
  return (
    <div className="my-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
      {steps.map((s) => (
        <div key={s.n} className="rounded-md border border-line bg-elevated px-3 py-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber">
            {String(s.n).padStart(2, '0')}
          </div>
          <div className="mt-1 text-sm font-semibold leading-tight text-text break-words hyphens-auto">{s.name}</div>
          <div className="mt-1 text-xs leading-snug text-muted break-words hyphens-auto">{s.body}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- Section: Your Turn ----------

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      kind="your-turn"
      label="Your turn"
      eyebrow="No AI. Two minutes. Just write."
    >
      <div className="rounded-lg border border-amber/30 bg-amber/[0.03] p-6">
        <p className="text-text">
          Before you go to Lesson 1.2, write down your answers. No AI. Just you, two minutes, a few sentences each.
        </p>

        <ol className="mt-5 space-y-5">
          <YourTurnPrompt
            n={1}
            slug={LESSON_1_1_PROMPTS.STUDENT_AB}
            q="Of Student A and Student B, which one are you closer to right now, honestly?"
          />
          <YourTurnPrompt
            n={2}
            slug={LESSON_1_1_PROMPTS.SKIPPED}
            q="What is one specific way you have used AI in the last week where you skipped the thinking part?"
          />
          <YourTurnPrompt
            n={3}
            slug={LESSON_1_1_PROMPTS.DID_THINKING}
            q="What is one specific way you have used AI in the last week where you did the thinking first?"
          />
        </ol>

        <p className="mt-5 text-sm text-muted">
          There is no model answer for this one. It is a calibration. Your answers save to this browser as you type, and Lesson 8.5 reads them back so you can compare where you started to where you ended.
        </p>
      </div>
    </Section>
  );
}

function YourTurnPrompt({ n, slug, q }: { n: number; slug: string; q: string }) {
  const { value, onChange, onBlur, status } = useLocalAnswer('1.1', slug);
  return (
    <li>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          {String(n).padStart(2, '0')}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder="Your answer (saves to this browser as you type)…"
            rows={3}
            className="mt-3 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/70 focus:border-amber focus:outline-none"
          />
          <SaveIndicator status={status} hasValue={value.trim().length > 0} />
        </div>
      </div>
    </li>
  );
}

function SaveIndicator({
  status,
  hasValue,
}: {
  status: 'idle' | 'saving' | 'saved';
  hasValue: boolean;
}) {
  if (status === 'idle' && !hasValue) {
    return (
      <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted/70">
        Not started
      </p>
    );
  }
  if (status === 'saving') {
    return (
      <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted">
        Saving…
      </p>
    );
  }
  return (
    <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
      Saved to this browser
    </p>
  );
}

// ---------- Section: Quick Check ----------

function QuickCheck() {
  const questions = [
    {
      q: 'What are the three threads that run through every lesson in this program?',
      a: 'Real fluency, auditing AI, and posture (AI as a teammate, not a friend or authority).',
    },
    {
      q: 'What is the program betting on, in one sentence?',
      a: 'The students who win the next decade are the ones who think hardest before and about what AI gives them, not the ones who use AI the most.',
    },
    {
      q: 'Why is the "From last time" section in every lesson?',
      a: 'Because pulling information back out of memory is what makes it stick. Re-reading does not.',
    },
  ];

  return (
    <Section
      id="sec-quick-check"
      kind="quick-check"
      label="Quick check"
      eyebrow="Answer in your head, then reveal."
    >
      <p className="text-sm text-muted">
        Self-graded. No record. The point is to find what did not stick yet, not to score you.
      </p>
      <div className="mt-5 space-y-3">
        {questions.map((q, i) => (
          <QuickCheckItem key={i} n={i + 1} {...q} />
        ))}
      </div>
    </Section>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

// ---------- Section: Lock Card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      kind="lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Come back to it in 6 months."
    >
      <div className="overflow-hidden rounded-xl border border-amber/30 bg-gradient-to-br from-elevated to-surface" data-lockcard-body>
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
              Lesson 1.1 · Lock card
            </p>
            <button className="text-xs text-muted hover:text-text" title="Save snapshot">
              ⤓ Save image
            </button>
          </div>
          <p className="mt-4 text-lg leading-relaxed text-text">
            The 8 weeks teach three things: <strong className="text-text">real fluency, auditing AI, and posture</strong>. Three hours a week, depth over breadth. Every lesson follows the same shape: cold open, retrieval, teaching, your turn, check, lock.
          </p>
          <p className="mt-4 text-base text-muted">
            The students who win the next decade are the ones who think hardest <strong className="font-semibold text-text">before</strong> AI touches their work, and hardest <strong className="font-semibold text-text">about</strong> what AI gives them back.
          </p>
          <div className="mt-6">
            <ThreadDiagram />
          </div>
        </div>
      </div>

    </Section>
  );
}

function ThreadDiagram() {
  // Three threads running side-by-side across weeks 1-8
  const threads = [
    { name: 'Fluency', color: '#FFB547' },
    { name: 'Judgment', color: '#7DC8E8' },
    { name: 'Character', color: '#E89F8B' },
  ];
  const weekX = (i: number) => 80 + i * 50;
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        Threads across 8 weeks
      </p>
      <svg viewBox="0 0 500 140" className="mt-3 w-full" aria-hidden="true">
        {/* week ticks */}
        {Array.from({ length: 8 }).map((_, i) => (
          <g key={i}>
            <line
              x1={weekX(i)}
              x2={weekX(i)}
              y1="20"
              y2="110"
              stroke="#26262E"
              strokeWidth="1"
            />
            <text
              x={weekX(i)}
              y="128"
              fontSize="10"
              fill="#8A8A95"
              fontFamily="ui-monospace, monospace"
              textAnchor="middle"
            >
              W{i + 1}
            </text>
          </g>
        ))}
        {threads.map((t, i) => (
          <g key={t.name}>
            <text
              x="68"
              y={45 + i * 25}
              fontSize="11"
              fill={t.color}
              fontFamily="ui-monospace, monospace"
              textAnchor="end"
            >
              {t.name}
            </text>
            <line
              x1={weekX(0)}
              x2={weekX(7)}
              y1={41 + i * 25}
              y2={41 + i * 25}
              stroke={t.color}
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </g>
        ))}
      </svg>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 1.2
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            How AI Actually Works (No Metaphors)
          </h3>
          <p className="mt-2 text-sm text-muted">
            One good analogy. Then we peel it back to show the real mechanics. By the end you can explain it to your parents.
          </p>
          <p className="mt-2 text-xs text-muted">~40 min · 3 hands-on AI exercises</p>
        </div>
        <button className="shrink-0 rounded-md bg-amber px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-hover">
          Start →
        </button>
      </div>
    </div>
  );
}

// ---------- Shared Section primitive ----------

function Section({
  id,
  kind,
  label,
  eyebrow,
  children,
}: {
  id: string;
  kind: SectionKind;
  label: string;
  eyebrow: string;
  children: React.ReactNode;
}) {
  const kindColor: Record<SectionKind, string> = {
    'cold-open': 'text-amber',
    'from-last-time': 'text-[#7DC8E8]',
    teaching: 'text-text',
    'your-turn': 'text-amber',
    'quick-check': 'text-[#E89F8B]',
    'lock-card': 'text-amber',
  };
  return (
    <section id={id} className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0">
      <div className="mb-6">
        <p className={`font-mono text-[11px] uppercase tracking-[0.18em] ${kindColor[kind]}`}>
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function Subhead({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-10 text-xl font-semibold text-text first:mt-0">{children}</h2>
  );
}

// ---------- Left rail nav ----------

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: 'From last time' },
    { id: 'sec-teaching', label: 'Teaching' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-mono ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ---------- Icons ----------

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
